import { FC, useId } from "react"

import { ReactComponent as IconImage } from "@common_assets/svg/image-outlined.svg"
import {
  Avatar,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import { useTranslation } from "react-i18next"

import {
  Notice,
  Price,
  TableScrollRegion,
} from "../SendStoreChargeDialog.parts"
import {
  getLineTotal,
  ICartLine,
  LineStock,
} from "../SendStoreChargeDialog.utils"
import { reviewTableSx } from "./ReviewStep.styles"

interface IProps {
  cart: ICartLine[]
  lineStocks: LineStock[] | null
  isStockPending: boolean
  isRosterFallback: boolean
  shortLineCount: number
  isStockBlocking: boolean
  stockMessageId: string
}

const ReviewItemsTable: FC<IProps> = ({
  cart,
  lineStocks,
  isStockPending,
  isRosterFallback,
  shortLineCount,
  isStockBlocking,
  stockMessageId,
}) => {
  const { t } = useTranslation()
  const itemsHeadingId = useId()

  return (
    <Stack gap={1.5}>
      <Typography
        id={itemsHeadingId}
        variant="body1"
        fontWeight={700}
        color="mockup.neutral10"
      >
        {t("storeCharge.review.itemsHeading")}
      </Typography>
      <TableScrollRegion labelledBy={itemsHeadingId}>
        <Table
          size="small"
          sx={reviewTableSx}
          aria-labelledby={itemsHeadingId}
          aria-busy={isStockPending || undefined}
        >
          <TableHead>
            <TableRow>
              <TableCell component="th" scope="col">
                {t("storeCharge.review.colItem")}
              </TableCell>
              <TableCell component="th" scope="col" align="right">
                {t("storeCharge.review.colQty")}
              </TableCell>
              <TableCell component="th" scope="col">
                {t("storeCharge.review.colUnitPrice")}
              </TableCell>
              <TableCell component="th" scope="col">
                {t("storeCharge.review.colLineTotal")}
              </TableCell>
              <TableCell component="th" scope="col" align="right">
                {t("storeCharge.review.colStockRemaining")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cart.map((line, index) => {
              const stock = lineStocks?.[index]
              const isShort =
                stock?.kind === "short" || stock?.kind === "outOfStock"
              return (
                <TableRow
                  key={line.item.id}
                  sx={
                    isShort
                      ? {
                          bgcolor: isRosterFallback
                            ? "mockup.secondary90"
                            : "mockup.error90",
                        }
                      : undefined
                  }
                >
                  <TableCell component="th" scope="row">
                    <Stack direction="row" gap={1} alignItems="center">
                      <Avatar
                        src={line.item.icon_url ?? undefined}
                        alt=""
                        variant="rounded"
                        sx={{
                          width: "28px",
                          height: "28px",
                          flexShrink: 0,
                          bgcolor: "mockup.primary95",
                          color: "mockup.primary10",
                          "& svg": { width: "16px", height: "16px" },
                        }}
                      >
                        <IconImage />
                      </Avatar>
                      {line.item.name}
                    </Stack>
                  </TableCell>
                  <TableCell align="right">{line.quantity}</TableCell>
                  <TableCell>
                    <Stack alignItems="flex-start">
                      <Price amount={line.item.price} />
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack alignItems="flex-start">
                      <Price amount={getLineTotal(line)} />
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    {!stock ? (
                      isStockPending && <Skeleton aria-hidden />
                    ) : stock.kind === "ok" ? (
                      stock.remaining
                    ) : stock.kind === "unlimited" ? (
                      t("storeCharge.stockUnlimited")
                    ) : (
                      <Stack alignItems="flex-end">
                        <Typography
                          variant="body3"
                          color={
                            isRosterFallback
                              ? "mockup.neutral20"
                              : "mockup.error30"
                          }
                        >
                          {t("storeCharge.stock.notEnough")}
                        </Typography>
                        <Typography variant="body3" color="mockup.neutral20">
                          {stock.kind === "short"
                            ? t("storeCharge.stock.shortDetail", {
                                available: stock.available,
                                required: stock.required,
                              })
                            : t("store.outOfStock")}
                        </Typography>
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableScrollRegion>
      {isRosterFallback && shortLineCount > 0 && (
        <Notice tone="warning">
          <Typography variant="body3" color="mockup.neutral20">
            {t("storeCharge.stock.fallbackWarning")}
          </Typography>
        </Notice>
      )}
      {isStockBlocking && (
        <Notice id={stockMessageId} tone="error">
          <Typography variant="body3" color="mockup.error30">
            {t("storeCharge.stock.error", { count: shortLineCount })}
          </Typography>
        </Notice>
      )}
    </Stack>
  )
}

export default ReviewItemsTable
